import { useMemo } from 'react'
import { format } from 'date-fns'

import { parseTimeToHoursMinutes } from '../parseTimeToHoursMinutes'

type TimeLogEntry = { date?: Date; startTime?: string; endTime?: string }

type NormalizedInterval = { dateKey: string; start: number; end: number }

const normalize = (log: TimeLogEntry): NormalizedInterval | null => {
  if (!log.date || !log.startTime || !log.endTime) return null

  const start = parseTimeToHoursMinutes(log.startTime)
  const end = parseTimeToHoursMinutes(log.endTime)

  if (!start || !end) return null

  const startMinutes = start.hours * 60 + start.minutes
  const endMinutes = end.hours * 60 + end.minutes

  if (endMinutes <= startMinutes) return null

  return { dateKey: format(log.date, 'yyyy-MM-dd'), start: startMinutes, end: endMinutes }
}

const compareByDateThenStart = (a: NormalizedInterval, b: NormalizedInterval) => {
  if (a.dateKey !== b.dateKey) return a.dateKey < b.dateKey ? -1 : 1

  return a.start - b.start
}

export function useOverlappingTimeLogs(timeLogs: TimeLogEntry[]): { hasOverlap: boolean } {
  const hasOverlap = useMemo(() => {
    // Flatten, sort once by (date, start), then a single linear sweep: adjacent entries
    // on the same date overlap iff curr.start < prev.end. If no adjacent pair overlaps,
    // transitivity rules out any non-adjacent pair too. Overall O(n log n).
    const intervals = timeLogs
      .map(normalize)
      .filter((i): i is NormalizedInterval => i !== null)
      .sort(compareByDateThenStart)

    return intervals.some(
      (curr, i) => i > 0 && intervals[i - 1].dateKey === curr.dateKey && curr.start < intervals[i - 1].end
    )
  }, [timeLogs])

  return { hasOverlap }
}
